/**
 * Vercel AI SDK Data Stream Protocol Parser
 *
 * Parses the streaming protocol used by Cloudflare Agents (via Vercel AI SDK).
 * The protocol uses prefixed lines like "0:text" or "9:{...tool call json...}"
 *
 * @see https://sdk.vercel.ai/docs/ai-sdk-ui/stream-protocol
 */

/**
 * Mapping of protocol prefixes to human-readable types
 */
export const STREAM_TYPES = {
  0: 'text',
  2: 'data',
  3: 'error',
  8: 'message_annotation',
  9: 'tool_call',
  a: 'tool_call_streaming_start',
  b: 'tool_result',
  c: 'tool_call_delta',
  d: 'finish_message',
  e: 'finish_step',
  f: 'message_start',
};

/**
 * Parses a single line from the Vercel AI SDK Data Stream Protocol
 *
 * @param {string} line - A single line from the stream body (e.g., "0:\"Hello\"")
 * @returns {object|null} - Parsed data with type, prefix, and data, or null if invalid
 *
 * @example
 * parseDataStreamLine('0:"Hello"')
 * // => { type: 'text', prefix: '0', data: 'Hello' }
 *
 * @example
 * parseDataStreamLine('9:{"toolCallId":"call_123","toolName":"search","args":{}}')
 * // => { type: 'tool_call', prefix: '9', data: { toolCallId: 'call_123', toolName: 'search', args: {} } }
 */
export function parseDataStreamLine(line) {
  if (!line || line.trim() === '') {
    return null;
  }

  // Find the first colon (prefix is always before the first colon)
  const colonIndex = line.indexOf(':');
  if (colonIndex === -1) {
    return null;
  }

  const prefix = line.slice(0, colonIndex);
  const payload = line.slice(colonIndex + 1);

  // Parse the payload - it's JSON for most types
  let data;
  try {
    data = JSON.parse(payload);
  } catch {
    // Some payloads might be plain strings or malformed
    data = payload;
  }

  return {
    type: STREAM_TYPES[prefix] || 'unknown',
    prefix,
    data,
  };
}

/**
 * Parses an entire body string which may contain multiple protocol lines
 *
 * @param {string} body - The body field from the WebSocket message
 * @returns {array} - Array of parsed line objects
 *
 * @example
 * parseStreamBody('f:{"messageId":"msg-123"}\n0:"Hello"\n0:" world"\n')
 * // => [
 * //   { type: 'message_start', prefix: 'f', data: { messageId: 'msg-123' } },
 * //   { type: 'text', prefix: '0', data: 'Hello' },
 * //   { type: 'text', prefix: '0', data: ' world' }
 * // ]
 */
export function parseStreamBody(body) {
  if (!body) {
    return [];
  }

  const lines = body.split('\n').filter(Boolean);
  return lines.map(parseDataStreamLine).filter(Boolean);
}

/**
 * Check if a parsed item is a text chunk
 * @param {object} item - Parsed stream item
 * @returns {boolean}
 */
export function isTextChunk(item) {
  return item?.type === 'text';
}

/**
 * Check if a parsed item is a tool call
 * @param {object} item - Parsed stream item
 * @returns {boolean}
 */
export function isToolCall(item) {
  return item?.type === 'tool_call';
}

/**
 * Check if a parsed item is an error
 * @param {object} item - Parsed stream item
 * @returns {boolean}
 */
export function isError(item) {
  return item?.type === 'error';
}

/**
 * Check if a parsed item signals message completion
 * @param {object} item - Parsed stream item
 * @returns {boolean}
 */
export function isFinishMessage(item) {
  return item?.type === 'finish_message';
}

/**
 * Check if a parsed item is a data event
 * @param {object} item - Parsed stream item
 * @returns {boolean}
 */
export function isDataEvent(item) {
  return item?.type === 'data';
}

// ============================================================================
// Proposal Event Helpers
// ============================================================================

/**
 * Proposal event types that come through the data stream
 */
export const PROPOSAL_EVENT_TYPES = {
  PROPOSAL_CREATED: 'proposalCreated',
  PROPOSAL_ERROR: 'proposalError',
};

/**
 * Check if a data event is a proposal event (any type)
 * @param {object} event - The data payload (not the parsed item)
 * @returns {boolean}
 */
export function isProposalEvent(event) {
  return (
    event?.type === PROPOSAL_EVENT_TYPES.PROPOSAL_CREATED ||
    event?.type === PROPOSAL_EVENT_TYPES.PROPOSAL_ERROR
  );
}

/**
 * Check if a data event is a proposal created event
 * @param {object} event - The data payload
 * @returns {boolean}
 */
export function isProposalCreatedEvent(event) {
  return event?.type === PROPOSAL_EVENT_TYPES.PROPOSAL_CREATED;
}

/**
 * Check if a data event is a proposal error event
 * @param {object} event - The data payload
 * @returns {boolean}
 */
export function isProposalErrorEvent(event) {
  return event?.type === PROPOSAL_EVENT_TYPES.PROPOSAL_ERROR;
}

/**
 * Extract all proposal events from a parsed stream item
 *
 * @param {object} item - Parsed stream item (from parseDataStreamLine)
 * @returns {array} - Array of proposal events, or empty array if none
 */
export function extractProposalEvents(item) {
  if (item?.type !== 'data') return [];

  const events = Array.isArray(item.data) ? item.data : [item.data];

  return events.filter(
    (event) =>
      event?.type === PROPOSAL_EVENT_TYPES.PROPOSAL_CREATED ||
      event?.type === PROPOSAL_EVENT_TYPES.PROPOSAL_ERROR
  );
}

// ============================================================================
// Page Builder Event Helpers
// ============================================================================

/**
 * Page builder event types that come through the data stream
 */
export const PAGE_BUILDER_EVENT_TYPES = {
  PAGE_START: 'pageStart',
  PAGE_STATUS: 'pageStatus',
  PAGE_OPERATION: 'pageOperation',
};

/**
 * Check if a parsed item is a page builder event (any type)
 * Page builder events come through the 'data' type with specific event types
 *
 * @param {object} item - Parsed stream item
 * @returns {boolean}
 */
export function isPageBuilderEvent(item) {
  if (item?.type !== 'data') return false;

  // Data events can be arrays or single objects
  const events = Array.isArray(item.data) ? item.data : [item.data];
  return events.some(
    (event) =>
      event?.type === PAGE_BUILDER_EVENT_TYPES.PAGE_START ||
      event?.type === PAGE_BUILDER_EVENT_TYPES.PAGE_STATUS ||
      event?.type === PAGE_BUILDER_EVENT_TYPES.PAGE_OPERATION
  );
}

/**
 * Check if a data event is a page start event
 * @param {object} event - The data payload (not the parsed item)
 * @returns {boolean}
 */
export function isPageStartEvent(event) {
  return event?.type === PAGE_BUILDER_EVENT_TYPES.PAGE_START;
}

/**
 * Check if a data event is a page status event
 * @param {object} event - The data payload (not the parsed item)
 * @returns {boolean}
 */
export function isPageStatusEvent(event) {
  return event?.type === PAGE_BUILDER_EVENT_TYPES.PAGE_STATUS;
}

/**
 * Check if a data event is a page operation event
 * @param {object} event - The data payload (not the parsed item)
 * @returns {boolean}
 */
export function isPageOperationEvent(event) {
  return event?.type === PAGE_BUILDER_EVENT_TYPES.PAGE_OPERATION;
}

/**
 * Extract all page builder events from a parsed stream item
 *
 * @param {object} item - Parsed stream item (from parseDataStreamLine)
 * @returns {array} - Array of page builder events, or empty array if none
 *
 * @example
 * const parsed = parseDataStreamLine('2:[{"type":"pageOperation","pageId":"abc","operation":{...}}]');
 * const pageEvents = extractPageBuilderEvents(parsed);
 * // => [{ type: 'pageOperation', pageId: 'abc', operation: {...} }]
 */
export function extractPageBuilderEvents(item) {
  if (item?.type !== 'data') return [];

  // Data events can be arrays or single objects
  const events = Array.isArray(item.data) ? item.data : [item.data];

  return events.filter(
    (event) =>
      event?.type === PAGE_BUILDER_EVENT_TYPES.PAGE_START ||
      event?.type === PAGE_BUILDER_EVENT_TYPES.PAGE_STATUS ||
      event?.type === PAGE_BUILDER_EVENT_TYPES.PAGE_OPERATION
  );
}
